import axios from 'axios'

// Runtime env from /env.js (injected at deploy), with Vite dev fallback
const env = (window as any).__ARIA_ENV__ || {}
const TENANT_URL = env.TENANT_URL || import.meta.env.VITE_TENANT_URL || '/api/tenant'
const SCANNER_URL = env.SCANNER_URL || import.meta.env.VITE_SCANNER_URL || '/api/scanner'
const ANALYSIS_URL = env.ANALYSIS_URL || import.meta.env.VITE_ANALYSIS_URL || '/api/analysis'
const ADMIN_URL = env.ADMIN_URL || import.meta.env.VITE_ADMIN_URL || '/api/admin'
const ANALYTICS_URL = env.ANALYTICS_URL || import.meta.env.VITE_ANALYTICS_URL || '/api/analytics'
const BILLING_URL = env.BILLING_URL || import.meta.env.VITE_BILLING_URL || '/api/billing'
const KB_URL = env.KB_URL || import.meta.env.VITE_KB_URL || '/api/kb'
const NOTIFICATION_URL = env.NOTIFICATION_URL || import.meta.env.VITE_NOTIFICATION_URL || '/api/notification'
const REPORT_URL = env.REPORT_URL || import.meta.env.VITE_REPORT_URL || '/api/report'

function createClient(baseURL: string) {
  const client = axios.create({ baseURL, headers: { 'Content-Type': 'application/json' } })
  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('token')
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  })
  // Auto-logout on 401 (expired or invalid token)
  // Skip for auth endpoints — login tries multiple services and expects 401s
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const url = error.config?.url || ''
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/signup')
        || url.includes('/auth/forgot-password') || url.includes('/auth/reset-password')
      if (error.response?.status === 401 && !isAuthEndpoint) {
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        window.location.href = '/'
      }
      return Promise.reject(error)
    }
  )
  return client
}

// Check token expiry on page load and every minute
function startTokenExpiryCheck() {
  const checkExpiry = () => {
    const token = localStorage.getItem('token')
    if (!token) return
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        window.location.href = '/'
      }
    } catch {}
  }
  checkExpiry()
  setInterval(checkExpiry, 60000) // check every minute
}
startTokenExpiryCheck()

const tenantClient = createClient(TENANT_URL)
const scannerClient = createClient(SCANNER_URL)
const analysisClient = createClient(ANALYSIS_URL)
const adminClient = createClient(ADMIN_URL)
const analyticsClient = createClient(ANALYTICS_URL)
const billingClient = createClient(BILLING_URL)
const kbClient = createClient(KB_URL)
const notificationClient = createClient(NOTIFICATION_URL)
const reportClient = createClient(REPORT_URL)

// Tenant API
export const demoApi = {
  // Public — no auth. The form is for prospects, who by definition have no
  // account. Nothing here reads a demo record back; every read of that data
  // is platform-admin gated on the server.
  kbCoverage: () => tenantClient.get('/demo/kb-coverage'),
  createDraft: () => tenantClient.post('/demo-requests/draft'),
  saveDraft: (token: string, data: any) =>
    tenantClient.patch(`/demo-requests/draft/${token}`, data),
  submit: (data: any) => tenantClient.post('/demo-requests', data),

  // Platform admin only.
  list: (params?: any) => tenantClient.get('/demo-requests', { params }),
  get: (id: string) => tenantClient.get(`/demo-requests/${id}`),
  update: (id: string, data: any) => tenantClient.patch(`/demo-requests/${id}`, data),
  log: (data: any) => tenantClient.post('/demo-requests/logged', data),
  remove: (id: string) => tenantClient.delete(`/demo-requests/${id}`),
}

export const tenantApi = {
  // Install-level licence state. Returns { licensed: false } on the hosted
  // product, where entitlement comes from the subscription tier instead.
  getLicenceStatus: () => tenantClient.get('/licence/status'),
  getLicenceCeiling: () => tenantClient.get('/licence/ceiling'),
  createTenant: (data: any) => tenantClient.post('/tenants', data),
  getTenants: () => tenantClient.get('/tenants'),
  getTenant: (id: string) => tenantClient.get(`/tenants/${id}`),
  updateTenant: (id: string, data: any) => tenantClient.put(`/tenants/${id}`, data),
  deleteTenant: (id: string) => tenantClient.delete(`/tenants/${id}`),
  getTenantUsers: (id: string) => tenantClient.get(`/tenants/${id}/users`),
  createTenantUser: (id: string, data: any) => tenantClient.post(`/tenants/${id}/users`, data),
  updateTenantUser: (userId: string, data: any) => tenantClient.put(`/users/${userId}`, data),
  deleteTenantUser: (userId: string) => tenantClient.delete(`/users/${userId}`),
  resetTenantUserPassword: (userId: string, data: any) => tenantClient.post(`/users/${userId}/reset-password`, data),
  getTenantConfig: (id: string) => tenantClient.get(`/tenants/${id}/config`),
  updateTenantConfig: (id: string, data: any) => tenantClient.put(`/tenants/${id}/config`, data),
  // INT-1 — semantic intent detection toggle
  getSemanticIntent: (tenantId: string) =>
    tenantClient.get(`/tenants/${encodeURIComponent(tenantId)}/semantic-intent`),
  setSemanticIntent: (tenantId: string, enabled: boolean) =>
    tenantClient.put(`/tenants/${encodeURIComponent(tenantId)}/semantic-intent`, { enabled }),
  getScanner: (scannerId: string) => tenantClient.get(`/scanner/${scannerId}`),
  getTenantScanners: (tenantId: string) => tenantClient.get(`/tenants/${tenantId}/scanners`),
  // Platform-admin cross-tenant inventory. Distinct from scannerApi.getAdminScanners,
  // which reports liveness from active_scanners and cannot see a scanner that was
  // provisioned but never registered. Returns no credentials.
  getScannerInventory: (params?: { status?: string; tenant_id?: string; limit?: number }) =>
    tenantClient.get('/admin/scanner-inventory', { params }),
  createScanner: (tenantId: string, data: any) => tenantClient.post(`/tenants/${tenantId}/scanners`, data),
  deleteScanner: (scannerId: string) => tenantClient.delete(`/scanners/${scannerId}`),
  rotateKeys: (scannerId: string) => tenantClient.post(`/scanners/${scannerId}/rotate-keys`),
  generateSetupToken: (scannerId: string) => tenantClient.post(`/scanners/${scannerId}/setup-token`),
  getDeploymentPackage: (scannerId: string) => tenantClient.get(`/scanners/${scannerId}/deployment`, { responseType: 'blob' }),
  getTenantQuotas: (tenantId: string) => tenantClient.get(`/tenants/${tenantId}/quotas`),
  enforceQuota: (tenantId: string, resource: string, quantity?: number) =>
    tenantClient.post(`/tenants/${tenantId}/enforce-quota`, null, { params: { resource, quantity: quantity || 1 } }),
  requestGracePeriod: (tenantId: string) => tenantClient.post(`/tenants/${tenantId}/request-grace-period`),
  setDesignPartner: (tenantId: string, durationDays?: number) =>
    tenantClient.post(`/tenants/${tenantId}/set-design-partner`, null, { params: { duration_days: durationDays || 30 } }),
  getPlatformInsights: () => tenantClient.get('/tenants/platform-insights'),
  verifyEmail: (token: string) => tenantClient.get(`/auth/verify-email`, { params: { token } }),
  // Team members (all users)
  getMembers: (tenantId: string) => tenantClient.get(`/tenants/${tenantId}/members`),
  // Role change requests
  requestRoleChange: (tenantId: string, data: any) => tenantClient.post(`/tenants/${tenantId}/role-requests`, data),
  getRoleRequests: (tenantId: string) => tenantClient.get(`/tenants/${tenantId}/role-requests`),
  approveRoleRequest: (tenantId: string, requestId: string) => tenantClient.post(`/tenants/${tenantId}/role-requests/${requestId}/approve`),
  rejectRoleRequest: (tenantId: string, requestId: string) => tenantClient.post(`/tenants/${tenantId}/role-requests/${requestId}/reject`),
  // Join requests
  getJoinRequests: (tenantId: string) => tenantClient.get(`/tenants/${tenantId}/join-requests`),
  approveJoinRequest: (tenantId: string, requestId: string) => tenantClient.post(`/tenants/${tenantId}/join-requests/${requestId}/approve`),
  rejectJoinRequest: (tenantId: string, requestId: string) => tenantClient.post(`/tenants/${tenantId}/join-requests/${requestId}/reject`),
  // Team settings
  getTeamSettings: (tenantId: string) => tenantClient.get(`/tenants/${tenantId}/team-settings`),
  updateTeamSettings: (tenantId: string, data: any) => tenantClient.put(`/tenants/${tenantId}/team-settings`, data),
  // Teams
  getTeams: (tenantId: string) => tenantClient.get(`/tenants/${tenantId}/teams`),
  createTeam: (tenantId: string, data: any) => tenantClient.post(`/tenants/${tenantId}/teams`, data),
  deleteTeam: (tenantId: string, teamId: string) => tenantClient.delete(`/tenants/${tenantId}/teams/${teamId}`),
  getTeamMembers: (tenantId: string, teamId: string) => tenantClient.get(`/tenants/${tenantId}/teams/${teamId}/members`),
  addTeamMember: (tenantId: string, teamId: string, data: any) => tenantClient.post(`/tenants/${tenantId}/teams/${teamId}/members`, data),
  removeTeamMember: (tenantId: string, teamId: string, userId: string) => tenantClient.delete(`/tenants/${tenantId}/teams/${teamId}/members/${userId}`),
  joinTeam: (tenantId: string, teamId: string) => tenantClient.post(`/tenants/${tenantId}/teams/${teamId}/join`),
  leaveTeam: (tenantId: string, teamId: string) => tenantClient.post(`/tenants/${tenantId}/teams/${teamId}/leave`),
  // SSO/IdP — §19 backend, frontend in S6-S8.
  // Reads/writes the per-tenant SSO config; client secret is encrypted server-side
  // and never returned in plaintext (GET returns '***' placeholder).
  getSsoConfig: (tenantId: string) => tenantClient.get(`/tenants/${tenantId}/sso/config`),
  upsertSsoConfig: (tenantId: string, data: any) => tenantClient.put(`/tenants/${tenantId}/sso/config`, data),
  deleteSsoConfig: (tenantId: string) => tenantClient.delete(`/tenants/${tenantId}/sso/config`),
  resolveSsoTenant: (email: string) => tenantClient.get(`/sso/resolve-tenant`, { params: { email } }),
  // Test Connection — admin opens authorization_url in popup, popup posts
  // result back via window.postMessage with {type:'arias_sso_test_result', payload:{...}}
  startSsoTestConnection: (data: { tenant_id: string; use_saved: boolean; draft?: any }) =>
    tenantClient.post(`/sso/test-connection/start`, data),
  getSsoAudit: (tenantId: string, params?: { since?: string; event_type?: string; outcome?: string; limit?: number; offset?: number }) =>
    tenantClient.get(`/tenants/${tenantId}/sso/audit`, { params }),
  // User-access management — §19 Phase 1.5
  listUsersAccess: (tenantId: string) => tenantClient.get(`/tenants/${tenantId}/users-access`),
  disablePasswordAuth: (userId: string) => tenantClient.post(`/users/${userId}/password-auth/disable`),
  enablePasswordAuth: (userId: string) => tenantClient.post(`/users/${userId}/password-auth/enable`),
  setBreakGlass: (userId: string) => tenantClient.post(`/users/${userId}/break-glass/set`),
  clearBreakGlass: (userId: string) => tenantClient.post(`/users/${userId}/break-glass/clear`),
  health: () => tenantClient.get('/health'),
}

// Scanner API
export const scannerApi = {
  register: (data: any) => scannerClient.post('/register', data),
  heartbeat: (data: any) => scannerClient.post('/heartbeat', data),
  submitFindings: (data: any) => scannerClient.post('/submit-findings', data),
  getConfig: () => scannerClient.get('/config'),
  getStatus: () => scannerClient.get('/status'),
  getAdminScanners: () => scannerClient.get('/admin/scanners'),
  getAdminSubmissions: () => scannerClient.get('/admin/submissions'),
  deleteScanner: (id: string) => scannerClient.delete(`/admin/scanner/${id}`),
  health: () => scannerClient.get('/health'),
  dryRunRule: (body: {
    pattern: string;
    exception_pattern?: string;
    name?: string;
    description?: string;
    severity?: string;
    category?: string;
    framework?: string;
    remediation?: string;
    rule_id?: string;
    scan_limit?: number;
  }) => scannerClient.post('/rules/dry-run', body),
}

// Analysis API
export const analysisApi = {
  processScanFindings: (data: any) => analysisClient.post('/process-scan-findings', data),
  analyze: (data: any) => analysisClient.post('/analyze', data),
  getAnalysis: (id: string) => analysisClient.get(`/analysis/${id}`),
  getAnalysisFindings: (id: string) => analysisClient.get(`/analysis/${id}/findings`),
  getAnalyses: (params?: any) => analysisClient.get('/analyses', { params }),
  deleteAnalysis: (id: string) => analysisClient.delete(`/analysis/${id}`),
  getAgentFingerprints: (analysisId: string) => analysisClient.get(`/analysis/${analysisId}/fingerprints`),
  getAgentFingerprintHistory: (agentId: string) => analysisClient.get(`/agent/${agentId}/fingerprint-history`),
  assistRule: (data: { description: string; framework?: string; severity?: string; dimension?: string }) =>
    analysisClient.post('/rules/assist', data),
  // Continuous-improvement loop — finding feedback (Phase 1)
  // Per-tenant: submit / read / delete the calling user's feedback on a finding.
  submitFindingFeedback: (findingId: string, data: { feedback_type: string; reason?: string }) =>
    analysisClient.post(`/findings/${findingId}/feedback`, data),
  getFindingFeedback: (findingId: string) =>
    analysisClient.get(`/findings/${findingId}/feedback`),
  deleteFindingFeedback: (findingId: string, feedbackId: string) =>
    analysisClient.delete(`/findings/${findingId}/feedback/${feedbackId}`),
  // Per-tenant aggregation: this tenant's full feedback history.
  listTenantFeedback: (tenantId: string, params?: { rule_id?: string; feedback_type?: string; since?: string }) =>
    analysisClient.get(`/tenants/${tenantId}/feedback`, { params }),

  // What the customer decided about a finding, in OpenVEX terms.
  //
  // Distinct from feedback above, which says whether ARIAS got the detection
  // right. This says what the customer is doing about it, and it is the half
  // that reaches compliance: a `not_affected` decision stops the finding
  // counting against a control, recomputes posture immediately, and travels
  // to the customer's GRC tool on their next poll.
  //
  // The decision attaches to the finding's cross-scan identity, so it survives
  // re-scanning — which is exactly when it matters, because the customer has
  // just acted on it.
  setFindingState: (
    findingId: string,
    data: {
      status: 'under_investigation' | 'affected' | 'not_affected' | 'fixed'
      // Required for not_affected; one of the CISA-minimum OpenVEX labels.
      justification?: string
      // Optional prose alongside the label, never instead of it.
      statement?: string
      expires_at?: string | null
    },
  ) => analysisClient.put(`/findings/${findingId}/state`, data),
  getFindingState: (findingId: string) =>
    analysisClient.get(`/findings/${findingId}/state`),
  // Platform aggregate (admin-only, anonymized — surfaces cross-tenant FP patterns).
  getAdminFeedbackAggregate: (since?: string) =>
    analysisClient.get('/admin/feedback/aggregate', { params: { since } }),
  // Compliance Posture — Phase 1
  getComplianceRepositories: () => analysisClient.get('/compliance/repositories'),
  getComplianceFrameworks: (repository?: string) =>
    analysisClient.get('/compliance/frameworks', { params: repository ? { repository } : undefined }),
  getCompliancePosture: (framework: string, repository?: string) =>
    analysisClient.get(`/compliance/posture/${encodeURIComponent(framework)}`,
      { params: repository ? { repository } : undefined }),
  getCompliancePostureHistory: (framework: string, repository?: string, limit = 30) =>
    analysisClient.get(`/compliance/posture/${encodeURIComponent(framework)}/history`,
      { params: { ...(repository ? { repository } : {}), limit } }),
  getComplianceControlDetail: (framework: string, controlId: string, repository?: string) =>
    analysisClient.get(`/compliance/posture/${encodeURIComponent(framework)}/${encodeURIComponent(controlId)}`,
      { params: repository ? { repository } : undefined }),
  // Phase 3a — Compliance API keys (admin-only)
  listComplianceApiKeys: () => analysisClient.get('/compliance/api-keys'),
  createComplianceApiKey: (label: string) =>
    analysisClient.post('/compliance/api-keys', { label }),
  revokeComplianceApiKey: (id: string) =>
    analysisClient.delete(`/compliance/api-keys/${encodeURIComponent(id)}`),
  // Phase 3a — Compliance webhooks (admin-only)
  listComplianceWebhooks: () => analysisClient.get('/compliance/webhooks'),
  createComplianceWebhook: (label: string, url: string) =>
    analysisClient.post('/compliance/webhooks', { label, url }),
  deleteComplianceWebhook: (id: string) =>
    analysisClient.delete(`/compliance/webhooks/${encodeURIComponent(id)}`),
  // §16 V1 Mesh — single-analysis topology
  getAnalysisMesh: (analysisId: string, params?: { lens?: string; framework?: string }) =>
    analysisClient.get(`/analysis/${encodeURIComponent(analysisId)}/mesh`, { params }),
  // §16 V1 Mesh — tenant-wide aggregated topology
  getTenantMesh: (
    tenantId: string,
    // repo_id is repeatable: axios serialises an array as repeated params,
    // which is what the endpoint reads. Topology does not use it — it fetches
    // the tenant mesh once and filters in the browser, so changing the
    // selection costs nothing — but the API takes several for callers that do.
    params?: { repo_id?: string | string[]; lens?: string; framework?: string },
  ) => analysisClient.get(`/tenants/${encodeURIComponent(tenantId)}/mesh`, { params }),
  // §16 V1 M5b — Compliance Lens drill-down
  getMeshComplianceContributors: (params: {
    framework: string
    control_id: string
    analysis_id?: string
  }) => analysisClient.get('/mesh/compliance/contributors', { params }),
  // §16 V1 UX4 — per-agent control list for lens-aware drawer
  getMeshComplianceAgentControls: (params: {
    framework: string
    agent_id_hint: string
    analysis_id?: string
  }) => analysisClient.get('/mesh/compliance/agent-controls', { params }),
  // §16 V1 M7a — drift timeline scan list
  getTenantMeshTimeline: (tenantId: string) =>
    analysisClient.get(`/tenants/${encodeURIComponent(tenantId)}/mesh/timeline`),
  // RBAC-7 — per-tenant scanner-version distribution for AnalyticsPage
  getTenantScannerVersions: (tenantId: string) =>
    analysisClient.get(`/tenants/${encodeURIComponent(tenantId)}/scanner-versions`),
  // INT-10 — per-tenant intent-classification distribution for AnalyticsPage
  getTenantIntentDistribution: (tenantId: string) =>
    analysisClient.get(`/tenants/${encodeURIComponent(tenantId)}/intent-distribution`),
  // §22 — cost estimation summed across a tenant's repos (latest scan per repo)
  getTenantCostRollup: (tenantId: string) =>
    analysisClient.get(`/tenants/${encodeURIComponent(tenantId)}/cost-rollup`),
  // §22 — cross-repo cost rankings (top agents, by-model spend, unbounded risk) for the Cost Analysis page
  getTenantCostRanking: (tenantId: string) =>
    analysisClient.get(`/tenants/${encodeURIComponent(tenantId)}/cost-ranking`),
  // Scan Comparison (Release Diff) — SD1
  listRepoScans: (
    repoIdentityHash: string,
    params?: { limit?: number; branch?: string },
  ) =>
    analysisClient.get(
      `/repos/${encodeURIComponent(repoIdentityHash)}/scans`,
      { params },
    ),
  getScanDiff: (a: string, b: string, includeUnchanged = false) =>
    analysisClient.get('/scan-diff', {
      params: { a, b, include_unchanged: includeUnchanged },
    }),
  // §16 V1 Mesh — per-node annotations (owner / team / criticality / tags / notes)
  getMeshAnnotation: (agentIdHint: string) =>
    analysisClient.get(`/mesh/annotations/${encodeURIComponent(agentIdHint)}`),
  upsertMeshAnnotation: (
    agentIdHint: string,
    data: {
      owner?: string | null
      team?: string | null
      criticality?: string | null
      tags?: string[] | null
      notes?: string | null
    },
  ) => analysisClient.post(`/mesh/annotations/${encodeURIComponent(agentIdHint)}`, data),
  deleteMeshAnnotation: (agentIdHint: string) =>
    analysisClient.delete(`/mesh/annotations/${encodeURIComponent(agentIdHint)}`),
  // §25 — model provenance. Declared rather than detected: whose model sits
  // behind an endpoint is not visible from the endpoint, so the organisation
  // says. Declaring clears PM-002; withdrawing reopens it.
  //
  // The vocabulary is fetched rather than hardcoded, so the list of origins
  // and what each one implies lives in one place on the server.
  getModelVocabulary: () => analysisClient.get('/models/vocabulary'),
  getModelDeclarations: () => analysisClient.get('/models/declarations'),
  declareModel: (
    identity: string,
    data: {
      component_kind: string
      origin: string
      base_models?: string[] | null
      modifications?: string[] | null
      notes?: string | null
    },
  ) => analysisClient.put(`/models/declarations/${encodeURIComponent(identity)}`, data),
  withdrawModelDeclaration: (identity: string, componentKind: string) =>
    analysisClient.delete(`/models/declarations/${encodeURIComponent(identity)}`, {
      params: { component_kind: componentKind },
    }),
  // §22 (2026-07-27) — per-agent volume assumption (expected daily requests).
  // Read-time overlay: setting/clearing this updates cost/day everywhere
  // immediately, no rescan needed.
  getAgentVolumeAssumption: (agentIdHint: string) =>
    analysisClient.get(`/agents/${encodeURIComponent(agentIdHint)}/volume-assumption`),
  upsertAgentVolumeAssumption: (agentIdHint: string, requestsPerDay: number) =>
    analysisClient.post(`/agents/${encodeURIComponent(agentIdHint)}/volume-assumption`, {
      requests_per_day: requestsPerDay,
    }),
  deleteAgentVolumeAssumption: (agentIdHint: string) =>
    analysisClient.delete(`/agents/${encodeURIComponent(agentIdHint)}/volume-assumption`),
  // §16 V1 Mesh — mis-detection feedback (node or edge)
  submitMeshFeedback: (data: {
    scope_type: 'node' | 'edge'
    scope_id: string
    feedback_type: string
    reason?: string
    analysis_id?: string
    edge_source_id?: string
    edge_target_id?: string
    edge_type?: string
    edge_detection?: string
    snapshot?: Record<string, unknown>
  }) => analysisClient.post('/mesh/feedback', data),
  health: () => analysisClient.get('/health'),
}

// Bring Your Own Framework (BYOF) — a tenant's own compliance framework.
// Enterprise entitlement `custom_framework`; every route 403s without it, so
// the UI gate below is presentation only and the backend remains the authority.

/** How a control is checked, if it is. `none` is a real, expected answer. */
export type EnforcedBy = 'conditions' | 'pattern' | 'none'

export interface ByofControlPreview {
  source_id: string
  title: string
  /** Evidence status: whether findings already exist in this control's categories. */
  status: 'covered' | 'no_evidence_yet' | 'not_bound'
  severity?: string | null
  categories: {
    category: string
    strength: 'primary' | 'supporting'
    confidence: 'high' | 'medium' | 'low'
    rationale: string
  }[]
  assertions: number
  evidence_count: number
  /** null when we could not judge enforceability at all — see `enforcement.assessable`. */
  enforced_by: EnforcedBy | null
  /** The check in words, e.g. "guardrails is not empty". */
  condition: string | null
  unenforceable_reasons: string[]
}

/**
 * Whether each control is actually checked by anything.
 *
 * `assessable` is false when the tenant has never scanned: what a rule can
 * check depends on what their agents report, so with no scan we say we cannot
 * tell rather than reporting every control as unenforced.
 */
export interface ByofEnforcement {
  assessable: boolean
  statement: string
  fields_available?: number
  rules?: number
  enforced_by_conditions?: number
  enforced_by_pattern?: number
  unenforced?: number
  unenforced_control_ids?: string[]
  skipped_no_category?: string[]
}

export interface ByofPreview {
  framework: {
    key: string
    display_name: string
    version?: string | null
    publisher?: string | null
    source_format?: string
    source_files?: string[]
  }
  detection: { format: string; confidence: number; evidence: string[]; shape?: string; reconsidered_from?: string }
  counts: { controls: number; assertions: number; bound: number; unbound: number; unparsed: number }
  coverage: {
    total_controls: number
    covered: number
    no_evidence_yet: number
    not_bound: number
    percent_of_bound_controls_covered: number
    denominator: string
  }
  controls: ByofControlPreview[]
  proposals: ByofProposal[]
  needs_review: ByofProposal[]
  unparsed: { reason?: string; [k: string]: unknown }[]
  diff: { added: string[]; removed: string[]; changed: string[]; unchanged: number } | null
  enforcement: ByofEnforcement
  notes: string[]
}

export interface ByofProposal {
  target: string
  value: unknown
  confidence: 'high' | 'medium' | 'low'
  evidence: string[]
  method: string
}

export interface ByofUploadResult {
  run_id: string
  document_id: string
  detection: ByofPreview['detection']
  readable: boolean
  preview?: ByofPreview
  proposals?: ByofProposal[]
  errors?: string[]
}

export interface ByofFramework {
  key: string
  display_name: string
  version?: string
  publisher?: string
  evidence_scope: 'augmented' | 'exclusive'
  controls: number
  citation?: string
}

export interface ByofControl {
  control_id: string
  control_name: string
  descriptor?: string | null
  rules: string[]
  checked: boolean
  /** How this control came to exist. A reader who cannot tell a control their
   *  document stated from one a rule implied cannot review their framework. */
  source: 'document' | 'rule' | 'manual'
  source_document?: string | null
}

export interface ByofVersion {
  id: string
  version: number
  label?: string | null
  note?: string | null
  created_at: string
  created_by?: string | null
  counts: { controls: number; checked: number; unchecked: number }
  statement?: string
}

/** What attaching a rule to a framework will actually draw in. */
export interface ByofEvidenceProjection {
  category: string | null
  findings_in_category: number
  evidence_scope: 'augmented' | 'exclusive'
  explanation: string
}

export const byofApi = {
  list: () => analysisClient.get<{ frameworks: ByofFramework[] }>('/byof/frameworks'),
  create: (data: {
    display_name: string
    version?: string
    publisher?: string
    evidence_scope?: 'augmented' | 'exclusive'
  }) => analysisClient.post<ByofFramework>('/byof/frameworks', data),
  update: (
    frameworkKey: string,
    data: {
      display_name?: string
      version?: string
      publisher?: string
      evidence_scope?: 'augmented' | 'exclusive'
    },
  ) => analysisClient.patch(`/byof/frameworks/${encodeURIComponent(frameworkKey)}`, data),
  remove: (frameworkKey: string) =>
    analysisClient.delete<{
      deleted: string
      controls: number
      rules: number
      rules_kept: string[]
      notes: string[]
    }>(`/byof/frameworks/${encodeURIComponent(frameworkKey)}`),

  /**
   * Add a document to an existing framework. Writes no controls — nothing
   * reaches the framework until the preview is approved, which is what makes
   * the review screen a decision rather than a summary.
   *
   * Re-uploading the same filename supersedes that document's earlier
   * versions; a different filename adds to the framework.
   */
  uploadDocument: (frameworkKey: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return analysisClient.post<ByofUploadResult>(
      `/byof/frameworks/${encodeURIComponent(frameworkKey)}/documents`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
  },
  approveDocument: (
    frameworkKey: string,
    runId: string,
    data: {
      field_mappings?: Record<string, unknown>
      severity_mappings?: Record<string, string>
      /** control_id -> the categories a reviewer chose, replacing ours. */
      category_overrides?: Record<string, string[]>
    } = {},
  ) =>
    analysisClient.post<{
      framework_key: string
      document_id: string
      controls: number
      controls_replaced: number
      rules: number
      rules_enforceable: number
      framework_controls: number
      framework_checked: number
      enforcement: string
      notes: string[]
    }>(
      `/byof/frameworks/${encodeURIComponent(frameworkKey)}/documents/${runId}/approve`,
      data,
    ),
  removeDocument: (frameworkKey: string, documentId: string) =>
    analysisClient.delete(
      `/byof/frameworks/${encodeURIComponent(frameworkKey)}/documents/${documentId}`,
    ),

  controls: (frameworkKey: string) =>
    analysisClient.get<{
      framework_key: string
      controls: ByofControl[]
      counts: { controls: number; checked: number; unchecked: number }
    }>(`/byof/frameworks/${encodeURIComponent(frameworkKey)}/controls`),

  /** Which frameworks a rule belongs to. Read-only, any signed-in user. */
  frameworksForRule: (ruleId: string) =>
    analysisClient.get<{
      rule_id: string
      frameworks: { key: string; display_name: string }[]
    }>(`/byof/rules/${encodeURIComponent(ruleId)}/frameworks`),
  addRule: (frameworkKey: string, ruleId: string) =>
    analysisClient.post<{
      control_id: string
      attached: boolean
      control_created: boolean
      evidence_projection: ByofEvidenceProjection
      framework_controls: number
    }>(`/byof/frameworks/${encodeURIComponent(frameworkKey)}/rules`, { rule_id: ruleId }),
  removeRule: (frameworkKey: string, ruleId: string) =>
    analysisClient.delete(
      `/byof/frameworks/${encodeURIComponent(frameworkKey)}/rules/${encodeURIComponent(ruleId)}`,
    ),

  /**
   * Create rules for controls that have none, once scans exist.
   *
   * Deliberately an action rather than something that happens on the next
   * scan: a rule appearing on its own would be a check nobody approved.
   */
  rebuildRules: (frameworkKey: string) =>
    analysisClient.post<{
      rules_created: number
      controls_already_covered: number
      framework_controls: number
      framework_checked: number
      statement: string
    }>(`/byof/frameworks/${encodeURIComponent(frameworkKey)}/rules/rebuild`),
  freezeVersion: (frameworkKey: string, data: { label?: string; note?: string }) =>
    analysisClient.post<ByofVersion>(
      `/byof/frameworks/${encodeURIComponent(frameworkKey)}/versions`,
      data,
    ),
  versions: (frameworkKey: string) =>
    analysisClient.get<{ versions: ByofVersion[] }>(
      `/byof/frameworks/${encodeURIComponent(frameworkKey)}/versions`,
    ),
  version: (frameworkKey: string, version: number) =>
    analysisClient.get(
      `/byof/frameworks/${encodeURIComponent(frameworkKey)}/versions/${version}`,
    ),

  /** Both scopes at once — see the note the endpoint returns with them. */
  comparePosture: (frameworkKey: string) =>
    analysisClient.get(`/byof/frameworks/${encodeURIComponent(frameworkKey)}/posture/compare`),
}

// Agent Reach — what an agent can actually touch, across every repository.
// Enterprise entitlement `agent_reach`; every route 404s without it, so the
// UI gate is presentation only and the backend remains the authority.

/** What kind of thing a component is, independent of vendor. */
export type ComponentKind =
  | 'vector_store' | 'search_engine' | 'graph_store' | 'managed_kb'
  | 'object_store' | 'hosted_store' | 'embedding_model'
  | 'mcp_server' | 'a2a_endpoint'
  /**
   * Something an agent retrieves from over HTTP, behind somebody else's
   * service. Deliberately not a store kind — the call says a retrieval
   * happens and says nothing about what is behind it.
   */
  | 'retrieval_endpoint'
  /**
   * The model layer: what agents think with. An inference endpoint is a way
   * through to a model and claims only that inference happens there, not which
   * model is behind it; a model artifact is the weights, checkpoint or
   * tokenizer a repository loads directly.
   */
  | 'inference_endpoint'
  | 'model_artifact'

export interface ReachDependent {
  agent_id_hint: string
  how: string
  source_file?: string | null
  /** Which repository this reach was observed in, where the scan named one. */
  repository?: string | null
  /**
   * How that repository scopes its retrievals against the component. `null`
   * where it made none and therefore has no opinion — deliberately distinct
   * from `unscoped`, which means we looked and found no scope.
   */
  scoping?: 'scoped' | 'unscoped' | 'mixed' | 'unknown' | null
  scoped_by?: 'constant' | 'variable' | 'namespace' | null
}

/**
 * Whether everything retrieving from a component agrees about who it may
 * answer for. The disagreement is the finding, and it is only visible once
 * scans are merged: neither repository can see the other caller.
 */
export interface ReachScoping {
  /** `unknown` means a query language we could not classify — deliberately
   *  distinct from `unscoped`, and it never counts as either side of a
   *  disagreement. */
  verdict: 'scoped' | 'unscoped' | 'mixed' | 'unknown' | null
  /** The weakest scope any caller used — a collection is only as isolated as
   *  its least careful one. */
  scoped_by: 'constant' | 'variable' | 'namespace' | null
  scoped_in: string[]
  unscoped_in: string[]
  /** Repositories whose retrievals we could not read either way. */
  unread_in: string[]
}

export interface ReachComponent {
  identity: string
  component_kind: ComponentKind
  declared_in: string[]
  sightings: number
  classification: string | null
  /** Set when the classification came from what filled this, not from itself. */
  classification_inherited_from: string | null
  classification_confidence: 'observed' | 'inferred' | null
  /**
   * How this became a component. `taxonomy` — a construct recognised by name.
   * `structural` — inferred from what the code does with it, which is how a
   * vendor outside our table, or a customer's own wrapper around one, becomes
   * visible at all. Weaker evidence, and it must never render as though it
   * were the stronger kind.
   */
  recognition: 'taxonomy' | 'structural'
  reached_by: ReachDependent[]
  dependent_count: number
  cross_repo: boolean
  scoping: ReachScoping
}

export interface ReachOverview {
  components: ReachComponent[]
  /** Components that are real but whose name we could not resolve. */
  unresolved_components: { component_kind: string; repository: string | null }[]
  /** Referenced by an agent, declared by no repository we have scanned. */
  reached_but_not_declared: string[]
  derivations: {
    from_identity: string
    to_identity: string
    basis: string
    confidence: string
  }[]
  coverage: {
    scans_considered: number
    components: number
    components_unresolved_identity: number
    reaches: number
    reached_but_not_declared: number
    /** An unscoped collection is not a defect — a single-tenant corpus is the
     *  ordinary case — so it is counted rather than reported. Only the
     *  disagreement is a finding. */
    collections_scoped: number
    collections_unscoped: number
    collections_scoping_disagrees: number
    collections_scoping_unread: number
    /**
     * Every claim about who may reach sensitive data rests on a tag the
     * customer wrote. Where nobody tagged, the claim is not "safe", it is
     * "unassessed", and these numbers are what keeps the two apart.
     */
    components_holding_data: number
    components_classified: number
    components_unclassified: number
    /** Classified because something that fills it is, not because anyone
     *  labelled it. Real evidence, weaker evidence, counted apart. */
    components_classification_inherited: number
  }
}

export interface ReachExposure {
  agent_id_hint: string
  agent_name: string | null
  identity: string
  component_kind: ComponentKind
  classification: string
  inherited_from: string | null
  confidence: 'observed' | 'inferred'
  how: string
  autonomy: string | null
  /** null means unassessed — we have no posture record for this agent. */
  no_approval_gate: boolean | null
}

export interface ConsistencyDefect {
  identity: string
  component_kind: ComponentKind
  property: string
  claims: { value: string; repositories: string[] }[]
  severity: 'high' | 'medium'
  what_it_means: string
}

/** One shared component, its stated monthly cost, and who depends on it. */
export interface ReachCostComponent {
  identity: string
  component_kind: ComponentKind
  declared_in: string[]
  dependent_count: number
  dependent_agents: string[]
  /** null means nobody has stated what this costs. Never treated as zero. */
  monthly_cost_usd: number | null
  cost_basis: string | null
  per_agent_monthly_usd: number | null
  /** Only ever 'even' today — said out loud rather than implied. */
  split: string | null
  cost_drivers: Record<string, string>
  /** Declared, but no scanned agent reaches it. */
  orphaned: boolean
}

/** One hosting layer, what it carries, and what the customer says it costs. */
export interface ReachPlatformCost {
  platform: string
  /** Prefixed so a platform can never share an identity with a component. */
  identity: string
  agents: number
  repositories: string[]
  services: string[]
  /** The platform grants tool access we cannot see from the code. */
  brokers_tool_auth: boolean
  monthly_cost_usd: number | null
  cost_basis: string | null
  per_agent_monthly_usd: number | null
}

export interface ReachPlatformCosts {
  platforms: ReachPlatformCost[]
  /** Counted and named: a real answer, not a detection gap. */
  self_hosted_agents: number
  totals: {
    monthly_attributed_usd: number
    platforms_priced: number
    platforms_unpriced: number
  }
  basis: string
}

export interface ReachCost {
  components: ReachCostComponent[]
  totals: {
    monthly_attributed_usd: number
    components_priced: number
    components_unpriced: number
    orphaned: number
  }
  /** The service's own words on what these figures are. Render, do not restate. */
  basis: string
}

export const reachApi = {
  overview: () => analysisClient.get<ReachOverview>('/reach'),
  exposure: () =>
    analysisClient.get<{
      exposures: ReachExposure[]
      counts: { total: number; ungated: number; unassessed: number }
    }>('/reach/exposure'),
  consistency: () =>
    analysisClient.get<{
      defects: ConsistencyDefect[]
      counts: { total: number; high: number }
    }>('/reach/consistency'),
  blastRadius: (identity: string) =>
    analysisClient.get(`/reach/blast-radius/${encodeURIComponent(identity)}`),
  cost: () => analysisClient.get<ReachCost>('/reach/cost'),
  platformCost: () => analysisClient.get<ReachPlatformCosts>('/reach/cost/platforms'),
  // Manager or administrator only, enforced server-side. A cost assumption is
  // an assertion colleagues will divide budgets by, so it carries a basis.
  setCost: (identity: string, monthly_cost_usd: number, basis?: string) =>
    analysisClient.put(`/reach/cost/${encodeURIComponent(identity)}`, {
      monthly_cost_usd, basis,
    }),
  clearCost: (identity: string) =>
    analysisClient.delete(`/reach/cost/${encodeURIComponent(identity)}`),
}

// ---------------------------------------------------------------------------
// Attack Path Analysis (ARIAS Exposure)
// ---------------------------------------------------------------------------
// All four are gated on `attack_paths`. Telemetry Gaps had its own key until
// 2026-10-01; the packaging decision put it inside Exposure, and a second key
// always granted alongside the first fails closed — the tenant who bought
// Exposure receives half of it.
//
// Call sites still use Promise.allSettled rather than Promise.all: one route
// failing for any reason — a timeout, a 500 — must not blank the others.
//
// Every route 404s without the entitlement, which is also what makes a blurred
// teaser safe: there is no payload to blur, so a teaser cannot leak what the
// tenant did not buy.

/** There is deliberately no value meaning "safe".
 *
 *  `not_reachable_in_scanned_code` is clumsy on purpose: dynamic dispatch, a
 *  tool loaded by name from an environment variable, or configuration injected
 *  at deploy all defeat static reachability, and the name has to carry that.
 *  Do not render it as a tick. */
export type InjectionVerdict =
  | 'reachable'
  | 'controlled'
  | 'not_reachable_in_scanned_code'
  | 'not_assessable'

export interface AttackPath {
  origin: string
  origin_kind: ComponentKind | null
  agent_id_hint: string
  verdict: InjectionVerdict
  hops: number
  /** Three states, and they are not interchangeable: a list of controls in
   *  force, `[]` for every hop examined and none found, `null` for at least
   *  one hop whose scope was never read. `null` is NOT "unguarded". */
  controls: string[] | null
  confidence: 'observed' | 'inferred' | null
  consequential_tools: string[]
  basis: {
    source_admits_external_content: boolean | null
    agent_holds_consequential_tool: boolean
    tools_of_unknown_tier: number
    controls_on_route: string[] | null
  }
}

export interface AttackPathsResponse {
  paths: AttackPath[]
  /** ALWAYS unfiltered, even when `verdict` narrowed the list. Render these
   *  separately from the rows, or the filter quietly becomes a claim that the
   *  rest is fine. */
  counts: Record<InjectionVerdict, number>
  /** What the negative verdict is worth. null when not entitled. */
  scope: string | null
  filtered_by: InjectionVerdict | null
}

export interface GuardrailCoverage {
  routes_considered: number
  counts: { guarded: number; unguarded: number; unknown: number }
  /** Over routes that were READ, not over all of them — so it moves when the
   *  estate changes rather than when our coverage does. `null` where nothing
   *  was readable; **never render null as 0%**, which would read as a
   *  failure rather than as an absence. */
  guarded_pct_of_read: number | null
  routes_read: number
  routes_unread: number
  chains_with_no_reader: number
  controls_in_use: { control: string; routes: number }[]
  unguarded: { origin: string; terminal: string; agent_id_hint: string; hops: number }[]
  /** Why the guarded figure can understate. Real, and demoted behind a
   *  disclosure: it used to be the whole of the help box, under a title that
   *  promised guidance and delivered an account of ARIAS's own internals. */
  limit?: string
  unknown: { origin: string; terminal: string; agent_id_hint: string; hops: number }[]
  /** The measured direction of error, carried on the answer because the
   *  number invites the opposite conclusion. Must be displayed, not dropped. */
  reading: string
  scope: string | null
}

export interface CounterfactualAgent {
  agent_id_hint: string
  directly_reaches: string[]
  would_influence: string[]
  component_count: number
  classified_count: number
  classified: string[]
  kinds: string[]
  max_hops: number
  /** Weakest route on the way out. `null` where the agent reaches nothing —
   *  a real answer, not a missing one. */
  confidence: 'observed' | 'inferred' | null
}

export const attackPathsApi = {
  /** `verdict` is validated server-side against the vocabulary; an
   *  unrecognised value is a 400 rather than an empty list, because "none
   *  found" and "you asked for something that does not exist" differ. */
  paths: (verdict?: InjectionVerdict) =>
    analysisClient.get<AttackPathsResponse>('/attack-paths', {
      params: verdict ? { verdict } : undefined,
    }),
  exposure: () =>
    analysisClient.get<{
      agents: CounterfactualAgent[]
      total: number
      truncated: boolean
      scope: string | null
    }>('/attack-paths/exposure'),
  guardrailCoverage: () =>
    analysisClient.get<GuardrailCoverage>('/attack-paths/guardrail-coverage'),
  // Telemetry Gaps. Shares `attack_paths` as of 2026-10-01 — it had its own
  // key until the packaging decision put it inside Exposure, and a second key
  // always granted beside the first fails closed.
  telemetryGaps: () => analysisClient.get('/attack-paths/telemetry-gaps'),
}

// Compliance Posture add-on (billing)
export const complianceAddonApi = {
  startCheckout: (data: { tenant_id: string; billing_email?: string; tenant_name?: string }) =>
    billingClient.post('/addons/compliance-posture/checkout', null, { params: data }),
}

// Per-tenant feature entitlements (billing).
// Read-only for tenant admins — provisioning is platform-admin only, so
// there is deliberately no update method exposed to the tenant UI.
export interface TenantFeature {
  key: string
  label: string
  description: string
  enabled: boolean
  // Which bundle sells it. Carried on the row so the UI groups by bundle
  // without holding its own copy of the mapping.
  bundle?: string | null
}

// A bundle is what a contract names; features are how it is implemented.
export interface TenantBundle {
  label: string
  description: string
  features: string[]
  features_active: string[]
  // ALL members resolving, not any of them.
  enabled: boolean
  // Some resolve and some do not — an incomplete grant, or a kill switch off
  // in this environment. The state that used to be invisible.
  partial: boolean
  // Core is false: it is granted to every eligible tenant and has no switch.
  grantable: boolean
}

export interface TenantEntitlements {
  tenant_id: string
  tier: string | null
  features: TenantFeature[]
  managed_by: string
  support_contact: string
  // Platform-admin only. `grants` is the raw stored state per feature;
  // `features[].enabled` is the resolved state after tier and kill-switch
  // checks. The provisioning UI needs both to avoid misrepresenting a
  // grant that exists but is currently suppressed.
  grants?: Record<string, boolean>
  tier_eligible?: boolean
  bundles?: Record<string, TenantBundle>
}

export const entitlementsApi = {
  get: (tenantId: string) =>
    billingClient.get<TenantEntitlements>(`/tenants/${tenantId}/entitlements`),
  // Platform admins only; the backend enforces Role.ADMIN.
  // `bundles` is the unit a contract names and is expanded server-side into
  // its member features, so enabling Exposure cannot deliver four of its five
  // keys. `features` still works, for repairing a partial grant.
  set: (
    tenantId: string,
    data: {
      features?: Record<string, boolean>
      bundles?: Record<string, boolean>
      note?: string
    },
  ) =>
    billingClient.put<TenantEntitlements>(`/tenants/${tenantId}/entitlements`, data),
}

// Admin API
export const adminApi = {
  getDashboardOverview: () => adminClient.get('/dashboard/overview'),
  getMetrics: () => adminClient.get('/metrics/current'),
  getMetricsHistory: () => adminClient.get('/metrics/history'),
  getServicesHealth: () => adminClient.get('/services/health'),
  getUsers: () => adminClient.get('/admin/users'),
  createUser: (data: any) => adminClient.post('/admin/users', data),
  deleteUser: (id: string) => adminClient.delete(`/admin/users/${id}`),
  resetUserPassword: (id: string, data: any) => adminClient.post(`/admin/users/${id}/reset-password`, data),
  getAlerts: () => adminClient.get('/alerts'),
  createAlert: (data: any) => adminClient.post('/alerts', data),
  acknowledgeAlert: (id: string) => adminClient.put(`/alerts/${id}/acknowledge`),
  resolveAlert: (id: string) => adminClient.put(`/alerts/${id}/resolve`),
  getAuditLogs: () => adminClient.get('/audit-logs'),
  getConfig: () => adminClient.get('/config'),
  updateConfig: (key: string, data: any) => adminClient.put(`/config/${key}`, data),
  health: () => adminClient.get('/health'),
}

// Analytics API
export const analyticsApi = {
  getTenantTrends: (tenantId: string) => analyticsClient.get(`/trends/tenant/${tenantId}`),
  getRepoTrends: (repoId: string) => analyticsClient.get(`/trends/repository/${repoId}`),
  // Trend lookup keyed by workspace_path_hash — used by AnalysisDetailPage
  // to overlay historical context on existing point-in-time cards.
  getWorkspaceTrends: (hash: string) => analyticsClient.get(`/trends/workspace/${hash}`),
  getTenantAssessments: (tenantId: string) => analyticsClient.get(`/assessments/tenant/${tenantId}`),
  getTenantRepos: (tenantId: string) => analyticsClient.get(`/repositories/tenant/${tenantId}`),
  getTenantMilestones: (tenantId: string) => analyticsClient.get(`/milestones/tenant/${tenantId}`),
  getTenantInsights: (tenantId: string) => analyticsClient.get(`/insights/tenant/${tenantId}`),
  getStats: () => analyticsClient.get('/stats'),
  health: () => analyticsClient.get('/health'),
  // ABF endpoints
  getTenantCertificates: (tenantId: string, params?: any) => analyticsClient.get(`/abf/certificates/tenant/${tenantId}`, { params }),
  // Material-change attestation. 404s without ARIAS Evidence, which the
  // certificates page reads to decide whether to explain the capability —
  // attestation had no surface at all before that.
  getAttestation: (tenantId: string) =>
    analyticsClient.get(`/abf/attestation/tenant/${tenantId}`),
  getCertificate: (certId: string) => analyticsClient.get(`/abf/certificate/${certId}`),
  // Renewal requires a reason (B1): it clears suspension, rotates the
  // baseline, resets the verdict and resets the 90-day clock — strictly more
  // than approve does — and is written to the same audit table.
  renewCertificate: (certId: string, body: { reason: string }) =>
    analyticsClient.post(`/abf/certificate/${certId}/renew`, body),
  // P0c (2026-06-01) — drift approval + audit
  approveDrift: (certId: string, body: { reason: string; drift_event_id?: string }) =>
    analyticsClient.post(`/abf/certificates/${certId}/approve`, body),
  listDriftApprovals: (certId: string) =>
    analyticsClient.get(`/abf/certificates/${certId}/approvals`),
  // P1a — bulk verdict map; mostly used server-side but exposed for ad-hoc dashboards.
  getLatestVerdicts: (tenantId: string) =>
    analyticsClient.get(`/abf/latest-verdicts/${tenantId}`),
  // P2c — drift verdict feedback loop
  submitDriftFeedback: (eventId: string, body: { feedback_type: string; reason?: string }) =>
    analyticsClient.post(`/abf/events/${eventId}/feedback`, body),
  getDriftFeedback: (eventId: string) =>
    analyticsClient.get(`/abf/events/${eventId}/feedback`),
  deleteDriftFeedback: (eventId: string) =>
    analyticsClient.delete(`/abf/events/${eventId}/feedback`),
  getTenantDriftFeedback: (tenantId: string, feedback_type?: string) =>
    analyticsClient.get(`/abf/feedback/tenant/${tenantId}`, { params: feedback_type ? { feedback_type } : undefined }),
  // P3 — explicit cert revocation (ADMIN only)
  revokeCertificate: (certId: string, body: { reason: string }) =>
    analyticsClient.post(`/abf/certificates/${certId}/revoke`, body),
  getDriftEvents: (tenantId: string, params?: any) => analyticsClient.get(`/abf/drift-events/tenant/${tenantId}`, { params }),
  getDriftSummary: (tenantId: string) => analyticsClient.get(`/abf/drift-summary/tenant/${tenantId}`),
  getAbfGate: (tenantId: string) => analyticsClient.get(`/abf/gate/tenant/${tenantId}`),
  exportAbf: (tenantId: string, format: string = 'json') => analyticsClient.get(`/abf/export/tenant/${tenantId}`, { params: { format } }),
  exportAll: (tenantId: string) => analyticsClient.get(`/export/tenant/${tenantId}`),
  // ACIA template management
  getACIATemplates: (params?: any) => analyticsClient.get('/acia/templates', { params }),
  createACIATemplate: (data: any) => analyticsClient.post('/acia/templates', data),
  updateACIATemplate: (id: string, data: any) => analyticsClient.put(`/acia/templates/${id}`, data),
  deleteACIATemplate: (id: string) => analyticsClient.delete(`/acia/templates/${id}`),
  // Agent Registry
  getAgentRegistry: (tenantId: string, params?: any) => analyticsClient.get(`/registry/${tenantId}`, { params }),
  getAgentRegistryCount: (tenantId: string) => analyticsClient.get(`/registry/${tenantId}/count`),
  getAgentRegistryEntry: (tenantId: string, agentId: string) => analyticsClient.get(`/registry/${tenantId}/agent/${agentId}`),
  updateAgentRegistryEntry: (tenantId: string, agentId: string, data: any) => analyticsClient.patch(`/registry/${tenantId}/agent/${agentId}`, data),
}

// Billing API
export const billingApi = {
  getPlans: () => billingClient.get('/plans'),
  getPlan: (id: string) => billingClient.get(`/plans/${id}`),
  createSubscription: (data: any) => billingClient.post('/subscriptions', data),
  getSubscription: (id: string) => billingClient.get(`/subscriptions/${id}`),
  getTenantSubscription: (tenantId: string) => billingClient.get(`/tenants/${tenantId}/subscription`),
  upgradeSubscription: (id: string, data: any) => billingClient.put(`/subscriptions/${id}/upgrade`, data),
  cancelSubscription: (id: string) => billingClient.post(`/subscriptions/${id}/cancel`),
  getTenantUsage: (tenantId: string) => billingClient.get(`/tenants/${tenantId}/usage`),
  getTenantInvoices: (tenantId: string) => billingClient.get(`/tenants/${tenantId}/invoices`),
  upgradeFromTrial: (data: { tenant_id: string; plan_id: string; billing_cycle?: string }) =>
    billingClient.post('/subscriptions/upgrade-from-trial', null, { params: data }),
  createUpgradeRequest: (data: { tenant_id: string; plan_id: string; billing_cycle?: string; tenant_name?: string; billing_email?: string }) =>
    billingClient.post('/upgrade-requests', null, { params: data }),
  // One bundle per request. Exposure and Evidence are sold independently, and
  // a single "contact us" would lose which one the customer asked for.
  requestBundle: (tenantId: string, bundle: string) =>
    billingClient.post('/bundle-requests', null, { params: { tenant_id: tenantId, bundle } }),
  getUpgradeRequests: (status?: string) =>
    billingClient.get('/upgrade-requests', { params: status ? { status } : {} }),
  generatePaymentLink: (requestId: string, amountOverride?: number) =>
    billingClient.post(`/upgrade-requests/${requestId}/generate-payment-link`,
      amountOverride ? { amount_override: amountOverride } : null),
  sendPaymentLink: (requestId: string, couponId?: string) =>
    billingClient.post(`/upgrade-requests/${requestId}/send-payment-link`,
      couponId ? { coupon_id: couponId } : null),
  getCancellationReasons: () => billingClient.get('/cancellation-reasons'),
  createCancellationRequest: (data: { tenant_id: string; tenant_name?: string; billing_email?: string; reason: string; reason_details?: string }) =>
    billingClient.post('/cancellation-requests', data),
  getCancellationRequests: (status?: string) =>
    billingClient.get('/cancellation-requests', { params: status ? { status } : {} }),
  approveCancellation: (requestId: string) =>
    billingClient.post(`/cancellation-requests/${requestId}/approve`),
  declineCancellation: (requestId: string) =>
    billingClient.post(`/cancellation-requests/${requestId}/decline`),
  withdrawCancellation: (requestId: string) =>
    billingClient.post(`/cancellation-requests/${requestId}/withdraw`),
  renewSubscription: (subscriptionId: string) =>
    billingClient.post(`/subscriptions/${subscriptionId}/renew`),
  health: () => billingClient.get('/health'),
}

// Knowledge Base API
export const kbApi = {
  createRule: (data: any, config?: any) => kbClient.post('/rules', data, config),
  getRule: (id: string) => kbClient.get(`/rules/${id}`),
  updateRule: (id: string, data: any) => kbClient.put(`/rules/${id}`, data),
  deleteRule: (id: string) => kbClient.delete(`/rules/${id}`),
  getRules: (params?: any) => kbClient.get('/rules', { params }),
  getRuleTemplates: () => kbClient.get('/rules/templates'),
  search: (data: any) => kbClient.post('/search', data),
  semanticSearch: (data: any) => kbClient.post('/semantic-search', data),
  getDimensionRules: (dimension: string) => kbClient.get(`/dimensions/${dimension}/rules`),
  getStats: () => kbClient.get('/stats'),
  getFrameworks: () => kbClient.get('/frameworks'),
  getDomains: () => kbClient.get('/domains'),
  // 3-level coverage tree (dimension → category → rule samples). Powers
  // the KnowledgeBasePage Coverage view that shows clients what we cover.
  getCoverage: () => kbClient.get('/coverage'),
  health: () => kbClient.get('/health'),
  // Validation endpoints
  validateRule: (id: string) => kbClient.post(`/rules/${id}/validate`),
  batchValidate: (ruleIds: string[]) => kbClient.post('/rules/batch-validate', { rule_ids: ruleIds }),
  // Self-learning endpoints
  getProposedPatterns: () => kbClient.get('/self-learning/proposed'),
  approvePattern: (ruleId: string) => kbClient.post(`/self-learning/approve/${ruleId}`),
  rejectPattern: (ruleId: string) => kbClient.post(`/self-learning/reject/${ruleId}`),
  getSelfLearningStats: () => kbClient.get('/self-learning/stats'),
}

// Notification API
export const notificationApi = {
  createChannel: (data: any) => notificationClient.post('/channels', data),
  getChannels: () => notificationClient.get('/channels'),
  send: (data: any) => notificationClient.post('/send', data),
  getNotifications: (params?: any) => notificationClient.get('/notifications', { params }),
  health: () => notificationClient.get('/health'),
}

// Report API
export const reportApi = {
  generate: (data: any) => reportClient.post('/generate', data),
  generateFromAnalysis: (analysisId: string, params?: any, extraHeaders?: any) =>
    reportClient.post(`/generate-from-analysis/${analysisId}`, null, { params, headers: extraHeaders }),
  getReport: (id: string) => reportClient.get(`/reports/${id}`),
  downloadReport: (id: string) => reportClient.get(`/reports/${id}/download`, { responseType: 'blob' }),
  getReports: (params?: any) => reportClient.get('/reports', { params }),
  deleteReport: (id: string) => reportClient.delete(`/reports/${id}`),
  getTemplates: () => reportClient.get('/templates'),
  getFormats: () => reportClient.get('/formats'),
  health: () => reportClient.get('/health'),
}

// Auth API — tries admin service first, then tenant service
export const authApi = {
  signup: (data: { name: string; company: string; email: string; password: string; terms_accepted?: boolean; terms_version?: string }) =>
    tenantClient.post('/auth/signup', data),
  forgotPassword: (email: string) =>
    tenantClient.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, new_password: string) =>
    tenantClient.post('/auth/reset-password', { token, new_password }),
  login: async (credentials: { username: string; password: string }) => {
    // Try admin service login first
    try {
      const res = await adminClient.post('/auth/login', credentials)
      return res
    } catch (adminErr: any) {
      // If admin login fails, try tenant user login
      try {
        const res = await tenantClient.post('/auth/login', credentials)
        return res
      } catch (tenantErr: any) {
        // Both failed — throw the most relevant error
        throw tenantErr
      }
    }
  },
  logout: () => {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    return Promise.resolve({ data: {} })
  },
  getCurrentUser: () => tenantClient.get('/auth/me'),
}

export default tenantClient
